import { Marketplaces } from "../../constants/common.ts";
import {
  ASIN_PARTIES,
  PLACEHOLDER_LAUNCH_ASIN_TYPES,
  PARTY_AGNOSTIC_ASIN_TYPES,
  FIRST_PARTY_ASIN_TYPES_EXTENDED,
  SECOND_PARTY_ASIN_TYPES_EXTENDED,
  THIRD_PARTY_ASIN_TYPES,
  WEBLAB_STATUS,
  BUYBOX_STATUS,
} from "../../constants/launchAsinTypes";
import { SelectProps } from "@amzn/awsui-components-react/polaris";
import { DateRangePickerProps } from "@amzn/awsui-components-react/polaris/date-range-picker/interfaces";

export function sortOptionsByLabelAlphabetical(options: SelectProps.Option[]) {
  return options.sort((a, b) => {
    if (a.label !== undefined && b.label !== undefined) {
      return a.label.localeCompare(b.label);
    } else {
      return 0;
    }
  });
}

export const toMultiSelectOption = (
  value: string | undefined,
  label: string | undefined
): SelectProps.Option => ({
  value: value || "",
  label: label || "",
});

export const toMultiSelectionOption = (value: string): SelectProps.Option => ({
  value: value,
  label: value,
});

export const toMultiSelectOptionsWithIndexCount = (
  options: SelectProps.Option[],
  indexCount: { [key: string]: { count: number } }
) => {
  const optionsWithRelatedData: SelectProps.Option[] = [];
  const optionsWithoutRelatedData: SelectProps.Option[] = [];
  options?.forEach((option) => {
    const count =
      option?.value &&
      option?.value in indexCount &&
      indexCount[option?.value].count > 0
        ? indexCount[option?.value].count
        : 0;
    const label = `${option.label} (${count})`;
    count > 0
      ? optionsWithRelatedData.push({ ...option, label })
      : optionsWithoutRelatedData.push({ ...option, label });
  });
  return [
    ...sortOptionsByLabelAlphabetical(optionsWithRelatedData),
    ...sortOptionsByLabelAlphabetical(optionsWithoutRelatedData),
  ];
};

export const MARKETPLACE_MULTI_SELECT_ITEMS: SelectProps.Options =
  sortOptionsByLabelAlphabetical(Marketplaces.map(toMultiSelectionOption));

export const ASIN_PARTY_SELECT_ITEMS: SelectProps.Options = ASIN_PARTIES.map(
  toMultiSelectionOption
);

export const WEBLAB_STATUS_SELECT_ITEMS: SelectProps.Options =
  WEBLAB_STATUS.map(toMultiSelectionOption);

export const BUYBOX_STATUS_SELECT_ITEMS: SelectProps.Options =
  BUYBOX_STATUS.map(toMultiSelectionOption);

export const VALIDATION_STATUS_ITEMS_PASS: {
  [validationStatus: string]: string;
} = {
  HATTRICK_PASSED: "Hattrick Passed",
  LVS_PASSED: "LVS Passed",
  MANUAL_DGL_PASSED: "DGL Passed",
  MANUAL_RL_PASSED: "RL Passed",
  MANUAL_INSTOCK_PASSED: "Instock Passed",
  MANUAL_OX_PASSED: "OX Passed",
  MANUAL_GTM_PASSED: "GTM Passed",
  MANUAL_CS_PASSED: "CS Passed",
};

export const VALIDATION_STATUS_ITEMS_PASS_EXCEPTION: {
  [validationStatus: string]: string;
} = {
  HATTRICK_PASSED_WITH_EXCEPTIONS: "Hattrick Passed with Exceptions",
  LVS_PASSED_WITH_EXCEPTIONS: "LVS Passed with Exceptions",
};

export const VALIDATION_STATUS_ITEMS_FAIL: {
  [validationStatus: string]: string;
} = {
  HATTRICK_FAILED: "Hattrick Failed",
  LVS_FAILED: "LVS Failed",
  MANUAL_DGL_FAILED: "DGL Failed",
  MANUAL_RL_FAILED: "RL Failed",
  MANUAL_INSTOCK_FAILED: "Instock Failed",
  MANUAL_OX_FAILED: "OX Failed",
  MANUAL_GTM_FAILED: "GTM Failed",
  MANUAL_CS_FAILED: "CS Failed",
};

export const BASELINE_STATUS_ITEMS_COMPLIANT: {
  [baselineStatus: string]: string;
} = {
  ANNOUNCE_DATE_COMPLIANT: "Announce Date Compliant",
  ONLINE_STREET_DATE_COMPLIANT: "Online Street Date Compliant",
};

export const BASELINE_STATUS_ITEMS_NON_COMPLIANT: {
  [baselineStatus: string]: string;
} = {
  ANNOUNCE_DATE_NON_COMPLIANT: "Announce Date Needs Review",
  ONLINE_STREET_DATE_NON_COMPLIANT: "Online Street Date Needs Review",
};

export const VALIDATION_STATUS_SELECT_ITEMS: SelectProps.Options = [
  {
    label: "Failed",
    options: Object.entries(VALIDATION_STATUS_ITEMS_FAIL).map(
      ([validationStatus, readableName]) => ({
        value: validationStatus,
        label: readableName,
      })
    ),
  },
  {
    label: "Passed",
    options: Object.entries(VALIDATION_STATUS_ITEMS_PASS).map(
      ([validationStatus, readableName]) => ({
        value: validationStatus,
        label: readableName,
      })
    ),
  },
  {
    label: "Passed (w/ Exceptions)",
    options: Object.entries(VALIDATION_STATUS_ITEMS_PASS_EXCEPTION).map(
      ([validationStatus, readableName]) => ({
        value: validationStatus,
        label: readableName,
      })
    ),
  },
];

export const BASELINE_COMPLIANCE_STATUS_MULTI_SELECT_ITEMS: SelectProps.Options =
  [
    {
      label: "No Review Needed",
      options: Object.entries(BASELINE_STATUS_ITEMS_COMPLIANT).map(
        ([baselineStatus, readableName]) => ({
          value: baselineStatus,
          label: readableName,
        })
      ),
    },
    {
      label: "Needs Review",
      options: Object.entries(BASELINE_STATUS_ITEMS_NON_COMPLIANT).map(
        ([baselineStatus, readableName]) => ({
          value: baselineStatus,
          label: readableName,
        })
      ),
    },
  ];

export const LAUNCH_ASIN_TYPE_MULTI_SELECT_ITEMS: SelectProps.Options = [
  {
    label: "Party Agnostic",
    options: Object.entries(PARTY_AGNOSTIC_ASIN_TYPES).map(
      ([asinTypeKey, readableName]) => ({
        value: asinTypeKey,
        label: readableName,
      })
    ),
  },
  {
    label: "1P",
    options: Object.entries(FIRST_PARTY_ASIN_TYPES_EXTENDED).map(
      ([asinTypeKey, readableName]) => ({
        value: asinTypeKey,
        label: readableName,
      })
    ),
  },
  {
    label: "2P",
    options: Object.entries(SECOND_PARTY_ASIN_TYPES_EXTENDED).map(
      ([asinTypeKey, readableName]) => ({
        value: asinTypeKey,
        label: readableName,
      })
    ),
  },
  {
    label: "3P",
    options: Object.entries(THIRD_PARTY_ASIN_TYPES).map(
      ([asinTypeKey, readableName]) => ({
        value: asinTypeKey,
        label: readableName,
      })
    ),
  },
  {
    label: "Placeholder",
    options: Object.entries(PLACEHOLDER_LAUNCH_ASIN_TYPES).map(
      ([asinTypeKey, readableName]) => ({
        value: asinTypeKey,
        label: readableName,
      })
    ),
  },
];

export const DATE_RANGE_I18N: DateRangePickerProps.I18nStrings = {
  todayAriaLabel: "Today",
  nextMonthAriaLabel: "Next month",
  previousMonthAriaLabel: "Previous month",
  customRelativeRangeDurationLabel: "Duration",
  customRelativeRangeDurationPlaceholder: "Enter duration",
  customRelativeRangeOptionLabel: "Custom range",
  customRelativeRangeOptionDescription: "Set a custom range in the future/past",
  customRelativeRangeUnitLabel: "Time unit",
  formatRelativeRange: (e) => {
    const t = 1 === e.amount ? e.unit : `${e.unit}s`;
    return `${e.amount > 0 ? "Next" : "Last"} ${Math.abs(e.amount)} ${t}`;
  },
  formatUnit: (e, t) => (1 === t ? e : `${e}s`),
  relativeModeTitle: "Relative range",
  absoluteModeTitle: "Absolute range",
  relativeRangeSelectionHeading: "Choose a range",
  startDateLabel: "Start date (Pacific Time)",
  endDateLabel: "End date (Pacific Time)",
  startTimeLabel: "Start time",
  endTimeLabel: "End time",
  clearButtonLabel: "Clear and dismiss",
  cancelButtonLabel: "Cancel",
  applyButtonLabel: "Apply",
};

export const DATE_RANGE_RELATIVE_OPTIONS: DateRangePickerProps.RelativeOption[] =
  [
    {
      key: "last-7-days",
      amount: -7,
      unit: "day",
      type: "relative",
    },
    {
      key: "next-7-days",
      amount: 7,
      unit: "day",
      type: "relative",
    },
    {
      key: "next-1-month",
      amount: 1,
      unit: "month",
      type: "relative",
    },
    {
      key: "next-6-months",
      amount: 6,
      unit: "month",
      type: "relative",
    },
  ];
